/**
 * FSE OtpInput — segmented digit boxes for OTP *and* PIN entry.
 *
 * Design source: `Documentation/Claude design files/preview/otp-input.html`.
 * Visual spec: N cells, each 64×80, 12px radius, 1.5px earth-900 border,
 * large readable digits (36px / weight 800 / tabular-nums). Empty cells use a
 * paper-100 background; the active cell shows a 3px leaf-700 focus ring.
 *
 * Mechanism: a single hidden, controlled `<input>` drives all visual cells.
 * This keeps caret/backspace/paste behaviour native and accessible while the
 * cells are pure presentation. In `mask` mode (PIN) each filled cell renders a
 * dot instead of the digit. Rural-low-vision audience: oversized targets and
 * high-contrast digits.
 *
 * Exposes a small, FSE-owned prop surface — MUI's `sx` is never public API.
 */
import { Box } from '@mui/material';
import { forwardRef, useImperativeHandle, useRef, type ChangeEvent, type ClipboardEvent } from 'react';
import { useColorMode } from '../theme/ColorMode.js';
import { mdSysColor, motion, paletteDark, paletteLight, radius } from '../theme/tokens.js';

export interface OtpInputProps {
  /** Number of digit cells. Default 4. */
  length?: number;
  /** Controlled value — the entered digits (length ≤ `length`). */
  value: string;
  /** Fires with the cleaned digit string on every change. */
  onChange: (value: string) => void;
  /** PIN mode — render dots instead of the actual digits. */
  mask?: boolean;
  /** Error state — cells render with the alert border + ring. */
  error?: boolean;
  /** Focus the hidden input on mount. */
  autoFocus?: boolean;
  /** Disable entry. */
  disabled?: boolean;
  /** Accessible label for the (hidden) input. Default "Verification code". */
  'aria-label'?: string;
}

export const OtpInput = forwardRef<HTMLInputElement, OtpInputProps>(function OtpInput(
  {
    length = 4,
    value,
    onChange,
    mask = false,
    error = false,
    autoFocus = false,
    disabled = false,
    'aria-label': ariaLabel = 'Verification code',
  },
  ref,
) {
  const { mode } = useColorMode();
  const sys = mdSysColor[mode];
  // Empty-cell digit tone — closest mode-aware role to the design's earth-300
  // (preview/otp-input.html): the theme's disabled-text token (earth[300] in light).
  const emptyDigitColor = (mode === 'dark' ? paletteDark : paletteLight).neutral.textDisabled;
  const inputRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => inputRef.current as HTMLInputElement, []);

  const digits = value.replace(/\D/g, '').slice(0, length);
  // The visually-active cell is the next empty slot (capped at the last cell).
  const activeIndex = Math.min(digits.length, length - 1);

  const focusInput = () => {
    if (!disabled) inputRef.current?.focus();
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value.replace(/\D/g, '').slice(0, length);
    onChange(next);
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (pasted) onChange(pasted);
  };

  return (
    <Box
      onClick={focusInput}
      sx={{
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        gap: 'clamp(6px, 2.5vw, 12px)',
        opacity: disabled ? 0.38 : 1,
        cursor: disabled ? 'default' : 'text',
      }}
    >
      {/* Hidden controlled input drives all cells (native caret/backspace/paste). */}
      <Box
        component="input"
        ref={inputRef}
        // numeric soft-keyboard + autofill of SMS codes where supported
        inputMode="numeric"
        autoComplete="one-time-code"
        type={mask ? 'password' : 'text'}
        value={digits}
        onChange={handleChange}
        onPaste={handlePaste}
        autoFocus={autoFocus}
        disabled={disabled}
        maxLength={length}
        aria-label={ariaLabel}
        aria-invalid={error || undefined}
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          opacity: 0,
          // Keep the caret inside the box stack but invisible — cells render state.
          border: 0,
          padding: 0,
          margin: 0,
          // letterSpacing keeps a masked caret from bunching; cosmetic only.
          letterSpacing: '64px',
          cursor: disabled ? 'default' : 'text',
        }}
      />
      {Array.from({ length }).map((_, i) => {
        const char = digits[i] ?? '';
        const filled = char !== '';
        const isFocused = !disabled && i === activeIndex;
        const borderColor = error ? sys.error : sys.onSurface;
        const ringColor = error ? sys.error : sys.primary;
        return (
          <Box
            key={i}
            aria-hidden
            sx={{
              flexGrow: 1,
              flexShrink: 1,
              flexBasis: 0,
              minWidth: 0,
              maxWidth: 64,
              aspectRatio: '64 / 80',
              border: `1.5px solid ${borderColor}`,
              borderRadius: `${radius.cornerMedium}px`,
              backgroundColor: filled ? sys.surface : sys.surfaceVariant,
              color: filled ? sys.onSurface : emptyDigitColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'inherit',
              fontVariantNumeric: 'tabular-nums',
              fontWeight: 800,
              fontSize: 'clamp(20px, 7vw, 36px)',
              lineHeight: 1,
              userSelect: 'none',
              transition: `box-shadow ${motion.fast}ms ${motion.ease}`,
              boxShadow: isFocused ? `0 0 0 3px ${ringColor}` : 'none',
            }}
          >
            {filled ? (mask ? '•' : char) : ''}
          </Box>
        );
      })}
    </Box>
  );
});
